/* eslint-disable array-callback-return */
import React from 'react';

import { Box, Button, Typography, Grid, CircularProgress } from '@mui/material';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CancelIcon } from '../../../common/assets/icons/cancel_icon_gray.svg';
import { ReactComponent as DownloadToFolderIcon } from '../../../common/assets/icons/download_to_folder_icon.svg';
import { FlorencePAGES } from '../../../common/components/tfa-auth/constants/pages';
import { useAppDispatch } from '../../../common/hooks/reduxHooks';
import { removeProcessedOcr } from '../../../common/stores/modelSlice';
import { useFileUpload } from '../../hooks/useFileUpload';
import { ProcessedOcr } from '../../types/ProcessedOcr';
import { SearchResultsParams } from '../../types/RouterState';

import BatchFileUploadCell from './BatchFileUploadCell';
import ScanButton from './ScanButton';

type Props = {
  clinicNotes?: boolean;
};

const BatchUploadContainer: React.FC<Props> = ({ clinicNotes }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    ocrs: processedOcrs,
    beginUpload,
    uploading,
    clearAll,
    savedNotes,
  } = useFileUpload();
  const scanTitle = 'Start scanning the documents';
  const submitTitle = 'Submit';

  const uploadingOcrsLength = React.useMemo(() => {
    return processedOcrs.filter((o) => o.isUploading).length;
  }, [processedOcrs]);

  const completedOcrsLength = React.useMemo(() => {
    return processedOcrs.filter((o) => o.uploadComplete).length;
  }, [processedOcrs]);

  const scanButtonDisabled = React.useMemo(() => {
    return _.isEmpty(processedOcrs) || uploadingOcrsLength > 0 || uploading;
  }, [processedOcrs, uploadingOcrsLength, uploading]);

  const scanButtonTitle = React.useMemo(() => {
    if (_.isEmpty(processedOcrs)) return scanTitle;
    return uploadingOcrsLength + completedOcrsLength === processedOcrs.length
      ? submitTitle
      : scanTitle;
  }, [uploadingOcrsLength, completedOcrsLength, processedOcrs]);

  const didTapClear = (ocr: ProcessedOcr) => {
    dispatch(removeProcessedOcr(ocr));
  };

  const didTapClearAll = () => {
    clearAll();
  };

  const submitScans = () => {
    navigate(FlorencePAGES.results, {
      state: {
        searchText: '',
        searchType: 'scan',
        notes: savedNotes,
      } as SearchResultsParams,
    });
  };

  const didTapScanButton = () => {
    scanButtonTitle === scanTitle ? beginUpload(!!clinicNotes) : submitScans();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        borderRadius: '16px',
        backgroundColor: !clinicNotes ? '#FBF9F4' : '#FFFFFF',
        px: '30px',
        py: '30px',
        minHeight: '500px',
        position: 'relative',
      }}
    >
      {uploading && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            paddingTop: '64px',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            zIndex: 99,
          }}
        >
          <CircularProgress color='primary' size='32px' />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          justifySelf: 'flex-start',
        }}
      >
        <Button
          onClick={didTapClearAll}
          disabled={uploading}
          variant='text'
          startIcon={<CancelIcon />}
        >
          <Typography color='text.secondary'>Cancel all</Typography>
        </Button>
        <Box>
          <ScanButton
            onClick={didTapScanButton}
            disabled={scanButtonDisabled}
            title={scanButtonTitle}
          />
        </Box>
      </Box>
      {processedOcrs.length !== 0 ? (
        <Grid
          container
          direction='row'
          justifyContent='space-between'
          marginTop={'30px'}
        >
          {[0, 1, 2].map((section) => (
            <Grid key={section} item>
              {processedOcrs.map((ocr, index) => {
                if (index % 3 === section) {
                  return (
                    <BatchFileUploadCell
                      key={ocr.id}
                      ocr={ocr}
                      handleTappedClear={didTapClear}
                    />
                  );
                }
              })}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <DownloadToFolderIcon />
          <Typography color='text.secondary' sx={{ mt: '18px' }}>
            No documents uploaded yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BatchUploadContainer;
